function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/unbound-method, no-param-reassign */ import { useRef, useMemo, useCallback, useState, useEffect } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { isEqual, uniq } from "lodash";
import Mousetrap from "mousetrap";
import { useHistory } from "react-router-dom";
import { ItemType } from "sprout-graphql";
import { isNotNullish } from "sprout-lib/dist/isNotNull";
import { scrollIntoView, forEachSafe, handledByList, sortItems, li2str, isActionValidForMultiselect, isActionValidForActiveObject } from "./utils";
import { ACTIONS, multiselectActionsFor } from "./actionRegistry";
import { CommandDispatchMode } from "./interfaces";
import { NavigationListContext } from "./NavigationListContext";
import MultiselectMenu from "./MultiselectMenu";
const soon = (callback)=>{
    if (typeof requestAnimationFrame === "function") return requestAnimationFrame(callback);
    else return window.setTimeout(callback, 1000 / 60);
};
const cancelSoon = (id)=>{
    if (typeof requestAnimationFrame === "function") return cancelAnimationFrame(id);
    else return window.clearTimeout(id);
};
const UP_KEYS = [
    "k",
    "up"
];
const DOWN_KEYS = [
    "j",
    "down"
];
const GROUP_UP_KEYS = [
    "{"
];
const GROUP_DOWN_KEYS = [
    "}"
];
const TOGGLE_MULTISELECT_MANY_KEYS = [
    "X",
    "shift+space"
];
const TOGGLE_MULTISELECT_KEYS = [
    ...TOGGLE_MULTISELECT_MANY_KEYS,
    "x", 
];
const CLEAR_MULTISELECT_KEYS = [
    "escape"
];
const SELECT_ALL_KEYS = [
    "mod+a"
];
const BASE_KEYS = [
    ...UP_KEYS,
    ...DOWN_KEYS,
    ...GROUP_UP_KEYS,
    ...GROUP_DOWN_KEYS,
    ...TOGGLE_MULTISELECT_KEYS,
    ...CLEAR_MULTISELECT_KEYS,
    ...SELECT_ALL_KEYS, 
];
//------------------------------------------------------------------------------
const isDev = process.env.NODE_ENV === "development";
//------------------------------------------------------------------------------
const ACTION_KEYS = ACTIONS.flatMap((a)=>a.keys);
const KEYS = [
    ...BASE_KEYS,
    ...ACTION_KEYS
];
const NavigationListProvider = (props)=>{
    const { children  } = props;
    const history = useHistory();
    const [pathname, setPathname] = useState(history.location.pathname);
    const [globalHandlers, setGlobalHandlers] = useState([]);
    const [lists, setLists] = useState([]);
    const [rawActiveCoord, rawSetActiveCoord] = useState(null);
    /*
   * BSG: this function looks like it doesn't do anything, looks like you could
   * just replace it with rawSetActiveCoord and there'd be no difference,
   * right? Right. Well... it apparently fixes a weird issue that prevents
   * keyboard (but not mouse) selection from working. I have literally no idea
   * why, and this is the most "voodoo" code I've written in the past few
   * years. Current suspicion is it relates to our abuse of useRef for
   * keyCallback below... but unsure. Either way, it fixes the bug... so it's
   * staying until we figure out what's going on for real.
   */ const setActiveCoord = useCallback((...args)=>rawSetActiveCoord(...args), []);
    // How many components are preventing active selection right now?
    const [locks, setLocks] = useState(0);
    const [forceMultiselect, rawSetForceMultiselect] = useState(false);
    const [rawMultiselectCoords, rawSetMultiselectCoords] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(false);
    const setForceMultiselect = useCallback((newForceMultiselect)=>{
        rawSetForceMultiselect(newForceMultiselect);
        if (newForceMultiselect && !rawMultiselectCoords) rawSetMultiselectCoords([]);
        if (!newForceMultiselect && rawMultiselectCoords) rawSetMultiselectCoords(null);
        setActionInProgress(false);
    }, [
        rawMultiselectCoords
    ]);
    const [lastMultiselectCoord, setLastMultiselectCoord] = useState(null);
    // When setting the multiselect to an empty array, instead set it `null`
    // unless `forceMultiselect` is set.
    const setMultiselectCoords = useCallback((valueOrCallback)=>{
        rawSetMultiselectCoords((oldValue)=>{
            const newValue = typeof valueOrCallback === "function" ? valueOrCallback(oldValue) : valueOrCallback;
            if (newValue && newValue.length === 0 && !forceMultiselect) {
                setLastMultiselectCoord(null);
                return null;
            } else {
                if (newValue == null && forceMultiselect) {
                    console.warn("Potential programming error! Must not set multiselectCoords to null when multiselect is forced. Assuming page transition and clearing forceMultiselect");
                    setForceMultiselect(false);
                }
                if (newValue === null) setLastMultiselectCoord(null);
                return newValue;
            }
        });
    }, [
        forceMultiselect,
        setForceMultiselect
    ]);
    useEffect(()=>{
        const listNavigationState = {
            activeCoord: rawActiveCoord,
            multiselectCoords: rawMultiselectCoords,
            forceMultiselect
        };
        history.replace(_objectSpread({}, history.location, {
            state: _objectSpread({}, history.location.state, {
                listNavigationState
            })
        }));
    }, [
        rawActiveCoord,
        rawMultiselectCoords,
        forceMultiselect,
        history
    ]);
    useEffect(()=>{
        return history.listen((listener)=>{
            const newPathname = listener.pathname;
            if (newPathname !== pathname) unstable_batchedUpdates(()=>{
                var ref;
                // Restore old state
                const state = (ref = listener.state) === null || ref === void 0 ? void 0 : ref.listNavigationState;
                var ref1;
                setActiveCoord((ref1 = state === null || state === void 0 ? void 0 : state.activeCoord) !== null && ref1 !== void 0 ? ref1 : null);
                rawSetMultiselectCoords(null); // state?.multiselectCoords ?? null
                rawSetForceMultiselect(false); // state?.forceMultiselect ?? false
                setLastMultiselectCoord(null);
                // Change pathname
                setPathname(newPathname);
            });
        });
    }, [
        history,
        pathname,
        setPathname,
        rawActiveCoord,
        rawMultiselectCoords,
        forceMultiselect,
        setActiveCoord, 
    ]);
    // const [tree, setTree] = useState<{ [listId: string]: string[] }>({});
    const [fullList, setFullList] = useState([]);
    const coordExists = useCallback((coord)=>!!fullList.find((listItem)=>listItem.list.listId === coord.listId && listItem.item.listItemId === coord.listItemId), [
        fullList
    ]);
    const activeCoordExists = !!rawActiveCoord && coordExists(rawActiveCoord);
    // If there's locks, or the relevant element hasn't rendered, pretend there's no active selection.
    const activeCoord1 = locks === 0 && activeCoordExists ? rawActiveCoord : null;
    const multiselectCoords = useMemo(()=>rawMultiselectCoords ? rawMultiselectCoords.filter(coordExists) : null, [
        coordExists,
        rawMultiselectCoords
    ]);
    const lastCounter = useRef(0);
    const counter = useRef(lastCounter.current + 1);
    const shouldThrottle = useCallback((event)=>{
        if (event.repeat && lastCounter.current === counter.current) // Reject repeated event within animation frame
        return true;
        lastCounter.current = counter.current;
        // Don't allow to run again until next animation frame
        soon(()=>{
            counter.current++;
        });
        return false;
    }, []);
    const navigateUpOrDown = useCallback((upOrDown, event)=>{
        const oldIdx = rawActiveCoord ? fullList.findIndex((li)=>li.list.listId === rawActiveCoord.listId && li.item.listItemId === rawActiveCoord.listItemId) : -1;
        const newIndex = upOrDown === "down" ? oldIdx + 1 : oldIdx - 1;
        if (newIndex >= fullList.length || newIndex < 0) return false;
        const li1 = fullList[newIndex];
        if (li1) {
            if (shouldThrottle(event)) return true;
            // This is on keyboard (rather than on element focus) because we
            // don't want mousing over to cause the page to scroll.
            scrollIntoView(li1.item.element);
            setActiveCoord({
                listId: li1.list.listId,
                listItemId: li1.item.listItemId
            });
            return true;
        }
        return false;
    }, [
        fullList,
        rawActiveCoord,
        shouldThrottle,
        setActiveCoord
    ]);
    const navigateGroupUpOrDown = useCallback((upOrDown, event)=>{
        if (shouldThrottle(event)) return true;
        const currentIndex = rawActiveCoord ? fullList.findIndex((l)=>l.list.listId === rawActiveCoord.listId && l.item.listItemId === rawActiveCoord.listItemId) : -1;
        const previousIndex = currentIndex >= 0 ? currentIndex - 1 : fullList.length - 1;
        const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 0;
        const l1 = fullList.length;
        const up = upOrDown === "up";
        for(let i = up ? previousIndex : nextIndex; up ? i >= 0 : i < l1; up ? i-- : i++){
            const li = fullList[i];
            if (li.list.options.groupContainer) {
                // Winner
                scrollIntoView(li.item.element);
                setActiveCoord({
                    listId: li.list.listId,
                    listItemId: li.item.listItemId
                });
                return true;
            }
        }
        return false;
    }, [
        shouldThrottle,
        rawActiveCoord,
        fullList,
        setActiveCoord
    ]);
    const listItemsBetween = useCallback((coordA, coordB)=>{
        const a = fullList.findIndex((i)=>i.list.listId === coordA.listId && i.item.listItemId === coordA.listItemId);
        const b = fullList.findIndex((i)=>i.list.listId === coordB.listId && i.item.listItemId === coordB.listItemId);
        if (a >= 0 && b >= 0) {
            const start = Math.min(a, b);
            const stop = Math.max(a, b);
            return fullList.slice(start, stop + 1).map((i)=>({
                    listId: i.list.listId,
                    listItemId: i.item.listItemId
                }));
        } else if (a >= 0) return [
            coordA
        ];
        else if (b >= 0) return [
            coordB
        ];
        else return [];
    }, [
        fullList
    ]);
    const toggleMultiselect = useCallback((// eslint-disable-next-line @typescript-eslint/no-shadow
    activeCoord, activeItem, many = false)=>{
        if (activeCoord && activeItem && activeItem.list.options.multiselect) {
            setMultiselectCoords((oldMultiselectCoords)=>{
                const multiselectIndex = activeCoord && oldMultiselectCoords ? oldMultiselectCoords.findIndex((c)=>c.listId === activeCoord.listId && c.listItemId === activeCoord.listItemId) : -1;
                if (multiselectIndex >= 0) {
                    if (!oldMultiselectCoords) // Appease TypeScript
                    throw new Error("Impossible");
                    if (many && lastMultiselectCoord) {
                        // Remove everything between (and including) last checkmark
                        const matches = listItemsBetween(lastMultiselectCoord, activeCoord);
                        const newMultiselectCoords = oldMultiselectCoords.filter((c)=>!matches.some((match)=>match.listId === c.listId && match.listItemId === c.listItemId));
                        return newMultiselectCoords;
                    } else {
                        // Remove just this one
                        const newMultiselectCoords = [
                            ...oldMultiselectCoords.slice(0, multiselectIndex),
                            ...oldMultiselectCoords.slice(multiselectIndex + 1), 
                        ];
                        return newMultiselectCoords;
                    }
                } else {
                    let newCoords = [];
                    // We might not use this in the end, this is just to see if it's possible
                    if (many && lastMultiselectCoord) // Remove everything between (and including) last checkmark
                    newCoords = listItemsBetween(lastMultiselectCoord, activeCoord);
                    else newCoords = [
                        activeCoord
                    ];
                    const newMultiselectCoords = [
                        ...oldMultiselectCoords !== null && oldMultiselectCoords !== void 0 ? oldMultiselectCoords : [],
                        ...oldMultiselectCoords ? newCoords.filter((c)=>// Only add new ones
                            !oldMultiselectCoords.some((match)=>match.listId === c.listId && match.listItemId === c.listItemId)) : newCoords, 
                    ];
                    // Determine if it's appropriate to add to the current multiselect
                    const newSelection = newMultiselectCoords.map((coord)=>{
                        const list = lists.find((l)=>l.listId === coord.listId);
                        if (!list) throw new Error("Invalid multiselect; list doesn't exist");
                        const item = list.items.find((i)=>i.listItemId === coord.listItemId);
                        if (!item) throw new Error("Invalid multiselect; item doesn't exist");
                        return item.options.object;
                    });
                    const validActions = multiselectActionsFor(newSelection);
                    if (validActions.length > 0) // Fine; select
                    return newMultiselectCoords;
                    else {
                        // Not allowed, maintain previous selection
                        console.warn("No valid multiselect actions, ignoring");
                        return oldMultiselectCoords;
                    }
                }
            });
            setLastMultiselectCoord(activeCoord);
            return true;
        } else return false;
    }, [
        lists,
        setMultiselectCoords,
        lastMultiselectCoord,
        listItemsBetween
    ]);
    const selectAll = useCallback(()=>{
        // NOTE: currently Chris has stated that only Post items should be affected by this key command.
        // Find all posts that can be multiselected and select them.
        const multiselectableEntries = lists.flatMap((list)=>{
            if (!list.options.multiselect) return [];
            return list.items.map((item)=>{
                const obj = item.options.object;
                if (obj.__typename !== "Item" || obj.type !== ItemType.Post) return null;
                return {
                    list,
                    item
                };
            }).filter(isNotNullish);
        });
        const newSelection = multiselectableEntries.map((o)=>o.item.options.object);
        const validActions = newSelection.length > 0 ? multiselectActionsFor(newSelection) : [];
        if (validActions.length > 0) {
            setMultiselectCoords(multiselectableEntries.map((o)=>({
                    listId: o.list.listId,
                    listItemId: o.item.listItemId
                })));
            return true;
        } else {
            console.info("Cannot multiselect - no appropriate selection");
            return false;
        }
    }, [
        lists,
        setMultiselectCoords
    ]);
    var _actions;
    const currentlyActiveCommands = useMemo(()=>uniq([
            ...globalHandlers.flatMap((h)=>h.actions),
            ...lists.flatMap((l)=>(_actions = l.options.actions) !== null && _actions !== void 0 ? _actions : []), 
        ]), [
        globalHandlers,
        lists
    ]);
    const multiselectObjects = useMemo(()=>{
        const itemForCoord = (coord)=>fullList.find((i)=>i.list.listId === coord.listId && i.item.listItemId === coord.listItemId);
        var ref;
        const multiselectItems = (ref = multiselectCoords === null || multiselectCoords === void 0 ? void 0 : multiselectCoords.map(itemForCoord).filter(isNotNullish)) !== null && ref !== void 0 ? ref : null;
        return multiselectItems === null || multiselectItems === void 0 ? void 0 : multiselectItems.map((match)=>{
            return match === null || match === void 0 ? void 0 : match.item.options.object;
        });
    }, [
        fullList,
        multiselectCoords
    ]);
    const activeItem1 = useMemo(()=>activeCoord1 ? fullList.find((i)=>i.list.listId === activeCoord1.listId && i.item.listItemId === activeCoord1.listItemId) : undefined, [
        activeCoord1,
        fullList
    ]);
    const activeObject = activeItem1 ? activeItem1.item.options.object : null;
    const multiselectHandled = useCallback((handled)=>{
        if (typeof handled === "boolean") // Clear the selection
        setMultiselectCoords(null);
        else {
            // `handled` must be a promise
            setActionInProgress(true);
            handled.then(()=>{
                // Success - clear state
                setMultiselectCoords(null);
                setActionInProgress(false);
            }, ()=>{
                // Failure - do not clear state
                setActionInProgress(false);
            });
        }
    }, [
        setMultiselectCoords
    ]);
    const dispatchCommand = useCallback((command, targetObject = activeObject, mode = CommandDispatchMode.Active | CommandDispatchMode.Multiselect | CommandDispatchMode.Standalone, /** If specified, implies that this action has already been validated */ rawAction, event)=>{
        if (!currentlyActiveCommands.includes(command)) {
            if (isDev) console.error(`Cannot dispatch command '${command}', it isn't active`);
            return false;
        }
        const items = multiselectObjects || (targetObject ? [
            targetObject
        ] : []);
        const action = rawAction || ACTIONS.find((a)=>a.command === command);
        if (!action) {
            if (isDev) console.error(`Cannot dispatch command '${command}', it isn't appropriate for the current situation`);
            return false;
        }
        const canMultiselect = !!(mode & CommandDispatchMode.Multiselect) && !!(multiselectObjects || targetObject) && isActionValidForMultiselect(action, items);
        const canActive = !!(mode & CommandDispatchMode.Active) && !!targetObject && isActionValidForActiveObject(action, targetObject);
        const canStandalone = !!(mode & CommandDispatchMode.Standalone) && action.standalone;
        let handled = false;
        const performDefault = ()=>{
            if (handled) return false;
            if (action.handle) {
                const result = action.handle(items, targetObject, event);
                handled = !!result;
                return result;
            } else return false;
        };
        const tryGlobal = (itemsToProcess)=>{
            for(let i = globalHandlers.length - 1; i >= 0; i--){
                const globalHandler = globalHandlers[i];
                const { actions , performAction  } = globalHandler;
                if (actions.includes(action.command)) {
                    const actionHandled = performAction(action.command, itemsToProcess, event, performDefault);
                    if (actionHandled) return actionHandled;
                }
            }
            return false;
        };
        // Try multiselect
        if (!handled && canMultiselect && multiselectCoords && multiselectCoords.length) {
            handled = tryGlobal(items);
            if (handled) multiselectHandled(handled);
        }
        // Because targetObject might differ from activeObject we cannot use
        // activeItem here; we must instead find the item based on the object.
        const targetItem = fullList.find((i)=>i.item.options.object === targetObject);
        // Try target object EVEN IF we're in multiselect mode.
        if (!handled && canActive && targetItem && targetObject) {
            const { list  } = targetItem;
            handled = handledByList(list, action.command, targetObject, event);
        }
        // Try global multiselect/single with target object
        if (!handled && (canMultiselect || canActive) && (!multiselectCoords || multiselectCoords.length === 0) && targetObject) handled = tryGlobal([
            targetObject
        ]);
        if (!handled && canStandalone && !targetItem) // No target item; find first list that supports this
        handled = lists.some((list)=>handledByList(list, action.command, targetObject, event));
        if (!handled && canStandalone && (!canMultiselect || !targetObject)) // Everything else has failed, try global standalone (unless we already tried that)
        handled = tryGlobal(items);
        if (!handled) handled = !!performDefault();
        if (!handled) {
            // Global failed
            if (isDev && !rawAction && !event) console.info(`The action '${action.command}' was triggered but had no successful handler.`);
            return false;
        }
        // TODO: should we really be cancelling all events? I think so... but
        // apparently this is "not usually a good practice" according to
        // Mousetrap docs... But... Surely you don't want two events triggered
        // by one keypress... :confused:
        event === null || event === void 0 ? void 0 : event.preventDefault();
        // Now the action has been performed; tell everyone about it.
        forEachSafe(lists, (l)=>{
            var _options, ref;
            return (ref = (_options = l.options).actionWasPerformed) === null || ref === void 0 ? void 0 : ref.call(_options, action.command, items);
        });
        forEachSafe(globalHandlers, (globalHandler)=>{
            var ref;
            return (ref = globalHandler.actionWasPerformed) === null || ref === void 0 ? void 0 : ref.call(globalHandler, action.command, items);
        });
        // This event was handled.
        return handled;
    }, [
        activeObject,
        currentlyActiveCommands,
        globalHandlers,
        lists,
        multiselectCoords,
        multiselectObjects,
        multiselectHandled,
        fullList, 
    ]);
    const keyCallback = (combo, event)=>{
        // In keyboard mode we don't want default browser events (e.g. Firefox's
        // radio toggling) to take effect, so we blur the active element.
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        // Builtin commands; note: if these commands don't apply they may choose to
        // not `return` giving chance for `actionRegistry` to handle the key
        // combinations. (This is useful for allowing `space` to toggle checklist
        // items, and for allowing `left/right` to navigate between post pages.)
        if (UP_KEYS.includes(combo)) {
            if (navigateUpOrDown("up", event)) {
                event.preventDefault();
                return;
            }
        }
        if (DOWN_KEYS.includes(combo)) {
            if (navigateUpOrDown("down", event)) {
                event.preventDefault();
                return;
            }
        }
        if (GROUP_UP_KEYS.includes(combo)) {
            if (navigateGroupUpOrDown("up", event)) {
                event.preventDefault();
                return;
            }
        }
        if (GROUP_DOWN_KEYS.includes(combo)) {
            if (navigateGroupUpOrDown("down", event)) {
                event.preventDefault();
                return;
            }
        }
        if (TOGGLE_MULTISELECT_KEYS.includes(combo)) {
            if (toggleMultiselect(activeCoord1, activeItem1, TOGGLE_MULTISELECT_MANY_KEYS.includes(combo))) {
                event.preventDefault();
                return;
            }
        }
        if (SELECT_ALL_KEYS.includes(combo)) {
            if (selectAll()) {
                event.preventDefault();
                return;
            }
        }
        // End of builtin commands; now for the actionRegistry
        const activeActionsForCombo = ACTIONS.filter((action)=>action.keys.includes(combo) && currentlyActiveCommands.includes(action.command));
        if (activeActionsForCombo.length > 0) {
            /*
       * To figure out what to do, we follow the search pattern:
       *
       * 1. If there's a multiselect in progress (and it's non-empty), find an
       *    action valid on the multiselect.
       * 2. Find an action valid on the current active item (non-multiselect) if
       *    there is one, even if multiselect is active.
       * 3. If multiselect is NOT in progress and there's an active item, see if
       *    there's a valid multiselect action on it.
       * 4. Fall back to standalone actions.
       *
       * At any point in the above, if the action is handled, stop. Otherwise continue.
       */ // Multiselect
            if (multiselectObjects && multiselectObjects.length > 0) {
                let handled = false;
                for(let i = 0, l = activeActionsForCombo.length; i < l && !handled; i++){
                    const action = activeActionsForCombo[i];
                    handled = isActionValidForMultiselect(action, multiselectObjects) && dispatchCommand(action.command, activeObject, CommandDispatchMode.Multiselect, action, event);
                }
                if (handled) {
                    multiselectHandled(handled);
                    return;
                }
            }
            // Single active object
            if (activeObject) {
                const handled = activeActionsForCombo.some((action)=>isActionValidForActiveObject(action, activeObject) && dispatchCommand(action.command, activeObject, CommandDispatchMode.Active, action, event));
                if (handled) return;
            }
            // Fake multiselect from single active object
            if (activeObject && !multiselectObjects) {
                const fakeMultiselectObjects = [
                    activeObject
                ];
                const handled = activeActionsForCombo.some((action)=>isActionValidForMultiselect(action, fakeMultiselectObjects) && dispatchCommand(action.command, activeObject, CommandDispatchMode.Multiselect, action, event));
                if (handled) return;
            }
            // Standalone
            const handled = activeActionsForCombo.some((action)=>action.standalone && dispatchCommand(action.command, activeObject, CommandDispatchMode.Standalone, action, event));
            if (handled) return;
        }
        // Lowest priority global handlers
        if (CLEAR_MULTISELECT_KEYS.includes(combo)) {
            if (multiselectCoords) {
                event.preventDefault();
                setMultiselectCoords(null);
                setForceMultiselect(false);
                return;
            } else if (rawActiveCoord) {
                setActiveCoord(null);
                return;
            }
        }
        if (isDev) {
            if (activeActionsForCombo.length > 0) console.info(`Key combination not handled: '${combo}' - no compatible actions found`);
            else console.info(`Key combination not handled: '${combo}' - no successful dispatches`);
        }
    };
    const keyCallbackRef = useRef(keyCallback);
    keyCallbackRef.current = keyCallback;
    const mouseInputModeRef = useRef(false);
    useEffect(()=>{
        // Mousetrap won't accept readonly arrays.
        const keys = uniq([
            ...KEYS
        ]);
        Mousetrap.bind(keys, (e, combo)=>{
            mouseInputModeRef.current = false;
            try {
                keyCallbackRef.current(combo, e);
            } catch (err) {
                console.error("Error occurred whilst processing keyCallback.current with ", combo);
                console.error(err);
            }
        });
        return ()=>{
            Mousetrap.unbind(keys);
        };
    }, []);
    useEffect(()=>{
        function checkTree() {
            const oldFullList = fullList;
            const allItems = lists.flatMap((list)=>list.items.map((item)=>({
                        list,
                        item
                    })));
            const newFullList = [
                ...allItems
            ].sort(sortItems);
            // - Check tree is in right order (reorder if not)
            if (!isEqual(oldFullList, newFullList)) setFullList(newFullList);
            const newCoords = newFullList.map(li2str);
            // - Check tree contains active item (select next relevant item if not)
            if (activeCoord1) {
                const oldIndex = oldFullList.findIndex((li)=>li.list.listId === activeCoord1.listId && li.item.listItemId === activeCoord1.listItemId);
                const newIndex = newFullList.findIndex((li)=>li.list.listId === activeCoord1.listId && li.item.listItemId === activeCoord1.listItemId);
                if (newIndex < 0) {
                    // Cannot find activeCoord any more.
                    // Find next possible coord
                    const nextListItem = oldFullList.find((li, index)=>index > oldIndex && newCoords.includes(li2str(li)));
                    const nextListItemIndex = nextListItem ? newFullList.findIndex((li)=>li.list.listId === nextListItem.list.listId && li.item.listItemId === nextListItem.item.listItemId) : -1;
                    const nextListItemPrevious = newFullList[nextListItemIndex - 1];
                    if (nextListItem) {
                        if (nextListItem && nextListItem.list.listId !== activeCoord1.listId && nextListItemPrevious && nextListItemPrevious.list.listId === nextListItem.list.listId) // Awkward workaround for todo issue when checking final item in list
                        setActiveCoord({
                            listId: nextListItemPrevious.list.listId,
                            listItemId: nextListItemPrevious.item.listItemId
                        });
                        else setActiveCoord({
                            listId: nextListItem.list.listId,
                            listItemId: nextListItem.item.listItemId
                        });
                    } else {
                        // Find previous possible coord
                        const previousListItems = oldFullList.filter((li, index)=>index < oldIndex && newCoords.includes(li2str(li)));
                        if (previousListItems.length) {
                            const previousListItem = previousListItems[previousListItems.length - 1];
                            setActiveCoord({
                                listId: previousListItem.list.listId,
                                listItemId: previousListItem.item.listItemId
                            });
                        } else if (newFullList.length > 0) {
                            // No possible coords found in old list; try and grab the first from new list
                            const firstListItem = newFullList[0];
                            setActiveCoord({
                                listId: firstListItem.list.listId,
                                listItemId: firstListItem.item.listItemId
                            });
                        } else // Nothing to select
                        setActiveCoord(null);
                    }
                }
            }
            // - Remove multiselectCoords that aren't in the tree
            const newMultiselectCoords = multiselectCoords ? multiselectCoords.filter((coord)=>newCoords.includes(`${coord.listId},${coord.listItemId}`)) : null;
            if (newMultiselectCoords != null && multiselectCoords != null && newMultiselectCoords.length !== multiselectCoords.length) setMultiselectCoords(newMultiselectCoords);
        }
        // We use a timeout here to give things time to settle.
        const timeout = soon(checkTree);
        return ()=>cancelSoon(timeout);
    }, [
        fullList,
        lists,
        multiselectCoords,
        activeCoord1,
        setMultiselectCoords,
        setActiveCoord, 
    ]);
    const lockNavigation = useCallback((lock)=>{
        if (lock) {
            setLocks((oldLocks)=>oldLocks + 1);
            return ()=>{
                setLocks((oldLocks)=>oldLocks - 1);
            };
        } else return undefined;
    }, []);
    // handlersToAdd, handlersToRemove, timeout, flushHandlers and
    // registerGlobalHandler work together to ensure that registerGlobalHandler
    // is efficient (we don't want it to call too many state updates).
    const handlersToAdd = useRef([]);
    const handlersToRemove = useRef([]);
    const timeout1 = useRef(null);
    const globalHandlersRef = useRef(globalHandlers);
    globalHandlersRef.current = globalHandlers;
    const setGlobalHandlersRef = useRef(setGlobalHandlers);
    setGlobalHandlersRef.current = setGlobalHandlers;
    const flushHandlers = useCallback(()=>{
        timeout1.current = null;
        const toAdd = handlersToAdd.current;
        const toRemove = handlersToRemove.current;
        handlersToAdd.current = [];
        handlersToRemove.current = [];
        const oldList = globalHandlersRef.current;
        const newList = uniq([
            ...oldList,
            ...toAdd
        ].filter((handler)=>!toRemove.includes(handler)));
        // If the lists don't contain the same items, update; otherwise maintain the old list even if the order has changed.
        if (newList.length !== oldList.length || !newList.every((item)=>oldList.includes(item))) setGlobalHandlersRef.current(newList);
    }, []);
    const registerGlobalHandler = useCallback((newGlobalHandler)=>{
        handlersToRemove.current = handlersToRemove.current.filter((cb)=>cb !== newGlobalHandler);
        handlersToAdd.current.push(newGlobalHandler);
        if (timeout1.current == null) timeout1.current = window.setTimeout(flushHandlers, 0);
        return ()=>{
            handlersToAdd.current = handlersToAdd.current.filter((cb)=>cb !== newGlobalHandler);
            handlersToRemove.current.push(newGlobalHandler);
            if (timeout1.current == null) timeout1.current = window.setTimeout(flushHandlers, 500);
        };
    }, [
        flushHandlers
    ]);
    const registerList = useCallback((listId, element, options, items)=>{
        setLists((oldLists)=>{
            const index = oldLists.findIndex((l)=>l.listId === listId);
            if (index < 0) return [
                ...oldLists,
                {
                    listId,
                    element,
                    options,
                    items
                }
            ];
            else throw new Error("This list id is already registered... Was it unique and stable?");
        });
        return ()=>{
            setLists((oldLists)=>{
                const index = oldLists.findIndex((l)=>l.listId === listId);
                if (index >= 0) return [
                    ...oldLists.slice(0, index),
                    ...oldLists.slice(index + 1)
                ];
                else throw new Error("This list id wasn't registered... Was it unique and stable?");
            });
        };
    }, []);
    const toggleItem = useCallback((listId, listItemId, selected)=>{
        setMultiselectCoords((oldMultiselectIdsBase)=>{
            const oldMultiselectIds = oldMultiselectIdsBase || [];
            const idx = oldMultiselectIds.findIndex((coord)=>coord.listId === listId && coord.listItemId === listItemId);
            const add = selected === true || selected == null && idx < 0;
            if (add) {
                if (idx >= 0) return oldMultiselectIds;
                return [
                    ...oldMultiselectIds,
                    {
                        listId,
                        listItemId
                    }
                ];
            } else {
                if (idx < 0) return oldMultiselectIds;
                return [
                    ...oldMultiselectIds.slice(0, idx),
                    ...oldMultiselectIds.slice(idx + 1), 
                ];
            }
        });
    }, [
        setMultiselectCoords
    ]);
    const value = useMemo(()=>({
            currentlyActiveCommands,
            dispatchCommand,
            forceMultiselect,
            setForceMultiselect,
            mouseInputModeRef,
            lockNavigation,
            registerGlobalHandler,
            registerList,
            activeCoord: activeCoord1,
            setActiveCoord,
            multiselectCoords,
            setMultiselectCoords,
            toggleItem
        }), [
        currentlyActiveCommands,
        dispatchCommand,
        forceMultiselect,
        setForceMultiselect,
        mouseInputModeRef,
        lockNavigation,
        registerGlobalHandler,
        registerList,
        activeCoord1,
        setActiveCoord,
        multiselectCoords,
        setMultiselectCoords,
        toggleItem, 
    ]);
    useEffect(()=>{
        function handleMouseMove() {
            mouseInputModeRef.current = true;
        }
        window.addEventListener("mousemove", handleMouseMove, false);
        return ()=>{
            window.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);
    return /*#__PURE__*/ _jsxs(NavigationListContext.Provider, {
        value: value,
        children: [
            children,
            multiselectObjects && multiselectObjects.length > 0 && !actionInProgress ? /*#__PURE__*/ _jsx(MultiselectMenu, {
                currentlyActiveCommands: currentlyActiveCommands,
                multiselectObjects: multiselectObjects
            }) : null
        ]
    });
};
export default NavigationListProvider;
